<script setup>
import { getCurrentInstance, ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import NewsletterEditModal from '@/components/NewsletterEditModal.vue';
import PreviewModal from '@/components/PreviewModal.vue';

defineOptions({ layout: AdminMainLayout });

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const LANGUAGES = ['pt', 'en', 'es'];

const NEWSLETTER_TYPES = [
    { key: 'maisVendidos', type: 'Best Sellers', icon: 'pi-star', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
    { key: 'destaques', type: 'Highlights', icon: 'pi-bookmark', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { key: 'customizado', type: 'Custom', icon: 'pi-palette', color: 'text-purple-600', bgColor: 'bg-purple-100' }
];

const typeToKey = {
    'Discount': 'desconto',
    'Best Sellers': 'maisVendidos',
    'Highlights': 'destaques',
    'Custom': 'customizado'
};

const props = defineProps({
    data: {
        type: Array,
        default: () => []
    },
    clients: {
        type: Array,
        default: () => []
    }
});

const NewsletterService = {
    get(id) {
        return axios.get(`/admin/newsletter/${id}`);
    },
    send(id, languages) {
        return axios.post(`/admin/newsletter/${id}/send`, { languages });
    }
};

const formatDate = (dateString) => {
    if (!dateString) return null;
    const datePart = dateString.includes('T')
        ? dateString.split('T')[0]
        : dateString.split(' ')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
};

const getActiveTranslations = (newsletter) =>
    newsletter?.translations?.filter(t => t.send_type && t.send_type !== null) || [];

const getConfiguredLanguages = (newsletter) =>
    newsletter?.translations?.filter(t => t.title && t.title.trim() !== '' && t.message && t.message.trim() !== '')?.map(t => t.lang) || [];

const getRecipientsSendType = (newsletter) => {
    const activeTranslations = getActiveTranslations(newsletter);
    if (activeTranslations.length === 0) return null;
    return activeTranslations[0].send_type;
};

const getSendTypeLabel = (sendType) => {
    const labels = {
        'All': $t('ALL preference only'),
        'Minimal': $t('ALL + MINIMAL'),
        'Disable': $t('ALL + MINIMAL + DISABLED'),
        'Custom': $t('Custom selection')
    };
    return labels[sendType] || sendType;
};

const createTemplateState = () => ({
    hasTemplate: false,
    hasRecipients: false,
    newsletterId: null,
    data: null,
    sendDate: null,
    templateConfiguredDate: null,
    configuredDate: null,
    configuredLanguages: [],
    sendType: null
});

const templateStates = ref({
    desconto: createTemplateState(),
    maisVendidos: createTemplateState(),
    destaques: createTemplateState(),
    customizado: createTemplateState()
});

const showEditModal = ref(false);
const showPreviewModal = ref(false);

const currentType = ref('');
const currentNewsletterId = ref(null);
const currentNewsletterData = ref(null);
const currentPreviewLanguage = ref(LANGUAGES[0]);
const availableLanguages = ref([]);

const isSending = ref({
    desconto: false,
    maisVendidos: false,
    destaques: false,
    customizado: false
});

const activeTabIndex = ref(0);

const syncNewsletterState = (newsletter) => {
    const key = typeToKey[newsletter.newsletter_type];
    if (!key || !templateStates.value[key]) return;

    templateStates.value[key] = {
        ...templateStates.value[key],
        hasTemplate: true,
        hasRecipients: getActiveTranslations(newsletter).length > 0,
        newsletterId: newsletter.id,
        data: newsletter,
        sendDate: formatDate(newsletter.send_date),
        templateConfiguredDate: formatDate(newsletter.template_configured_in),
        configuredDate: formatDate(newsletter.configured_in),
        configuredLanguages: getConfiguredLanguages(newsletter),
        sendType: getRecipientsSendType(newsletter)
    };
};

const loadNewsletters = () => {
    props.data.forEach(syncNewsletterState);
};

onMounted(loadNewsletters);

const openEditModal = (type) => {
    currentType.value = type;
    const key = typeToKey[type];
    currentNewsletterId.value = templateStates.value[key]?.newsletterId || null;
    currentNewsletterData.value = templateStates.value[key]?.data || null;
    showEditModal.value = true;
};

const openPreviewModal = (type) => {
    const key = typeToKey[type];
    const newsletter = templateStates.value[key]?.data;

    currentType.value = type;
    currentNewsletterId.value = templateStates.value[key]?.newsletterId || null;
    currentPreviewLanguage.value = LANGUAGES[0];

    if (newsletter?.translations) {
        availableLanguages.value = newsletter.translations
            .filter(t => t.title && t.title.trim() !== '' && t.message && t.message.trim() !== '')
            .map(t => t.lang);
    } else {
        availableLanguages.value = [];
    }

    showPreviewModal.value = true;
};

const handleNewsletterSaved = async (newsletterId) => {
    try {
        const { data } = await NewsletterService.get(newsletterId);
        if (data.success) syncNewsletterState(data.data);
        showEditModal.value = false;
    } catch (err) {
        console.error(err);
    }
};

const sendNewsletter = async (type) => {
    const key = typeToKey[type];
    const newsletter = templateStates.value[key]?.data;

    if (!newsletter) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Newsletter not found'),
            life: 5000
        });
        return;
    }

    const activeTranslations = getActiveTranslations(newsletter);

    if (!activeTranslations.length) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('No recipients configured'),
            life: 5000
        });
        return;
    }

    isSending.value[key] = true;

    try {
        toast.add({
            severity: 'info',
            summary: $t('Sending'),
            detail: $t('Sending newsletter...'),
            life: 3000
        });

        await NewsletterService.send(
            newsletter.id,
            activeTranslations.map(t => t.lang)
        );

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Newsletter sent successfully'),
            life: 5000
        });

        const { data } = await NewsletterService.get(newsletter.id);
        if (data.success) syncNewsletterState(data.data);

    } catch (err) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: err.response?.data?.message || $t('Error sending newsletter'),
            life: 5000
        });
    } finally {
        isSending.value[key] = false;
    }
};

const getNewsletterTypeConfig = (type) => {
    return NEWSLETTER_TYPES.find(t => t.type === type) || NEWSLETTER_TYPES[0];
};

const getTypeState = (type) => {
    const key = typeToKey[type];
    return templateStates.value[key];
};

const getStatusSeverity = (state) => {
    if (!state.hasTemplate) return 'danger';
    if (!state.hasRecipients) return 'warn';
    return 'success';
};

const getStatusLabel = (state) => {
    if (!state.hasTemplate) return $t('Not configured');
    if (!state.hasRecipients) return $t('No recipients');
    return $t('Ready to send');
};
</script>

<template>
    <div class="grid">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex items-center gap-3">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
                            <i class="pi pi-envelope text-primary-600 text-xl"></i>
                        </div>
                        <div>
                            <h1 class="text-xl font-bold text-surface-900 m-0">{{ $t('Newsletter Management') }}</h1>
                            <p class="text-sm text-surface-500 m-0 mt-1">{{ $t('Configure and send newsletters to your clients') }}</p>
                        </div>
                    </div>
                </template>

                <template #content>
                    <TabView v-model:activeIndex="activeTabIndex" class="newsletter-tabs">
                        <TabPanel v-for="config in NEWSLETTER_TYPES" :key="config.key">
                            <template #header>
                                <div class="flex items-center gap-2">
                                    <i :class="['pi', config.icon, config.color]"></i>
                                    <span>{{ $t(config.type) }}</span>
                                </div>
                            </template>

                            <div class="p-4">
                                <!-- Status Overview -->
                                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                        <div class="flex items-center gap-2 mb-2">
                                            <i class="pi pi-file-edit text-surface-500"></i>
                                            <span class="text-sm font-medium text-surface-600">{{ $t('Template') }}</span>
                                        </div>
                                        <div class="flex items-center justify-between">
                                            <Tag
                                                :severity="getTypeState(config.type).hasTemplate ? 'success' : 'danger'"
                                                :value="getTypeState(config.type).hasTemplate ? $t('Configured') : $t('Not configured')"
                                                rounded
                                            />
                                            <span v-if="getTypeState(config.type).templateConfiguredDate" class="text-xs text-surface-400">
                                                {{ getTypeState(config.type).templateConfiguredDate }}
                                            </span>
                                        </div>
                                        <div v-if="getTypeState(config.type).configuredLanguages.length > 0" class="mt-2 flex gap-1">
                                            <Tag
                                                v-for="lang in getTypeState(config.type).configuredLanguages"
                                                :key="lang"
                                                :value="lang.toUpperCase()"
                                                severity="info"
                                                rounded
                                                class="text-xs"
                                            />
                                        </div>
                                    </div>

                                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                        <div class="flex items-center gap-2 mb-2">
                                            <i class="pi pi-users text-surface-500"></i>
                                            <span class="text-sm font-medium text-surface-600">{{ $t('Recipients') }}</span>
                                        </div>
                                        <div class="flex items-center justify-between">
                                            <div v-if="getTypeState(config.type).hasRecipients">
                                                <div class="text-sm font-medium text-surface-700">
                                                    {{ getSendTypeLabel(getTypeState(config.type).sendType) }}
                                                </div>
                                            </div>
                                            <Tag
                                                v-else
                                                severity="warn"
                                                :value="$t('Not configured')"
                                                rounded
                                            />
                                            <span v-if="getTypeState(config.type).configuredDate" class="text-xs text-surface-400">
                                                {{ getTypeState(config.type).configuredDate }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                        <div class="flex items-center gap-2 mb-2">
                                            <i class="pi pi-calendar text-surface-500"></i>
                                            <span class="text-sm font-medium text-surface-600">{{ $t('Last Sent') }}</span>
                                        </div>
                                        <div class="text-lg font-semibold text-surface-700">
                                            {{ getTypeState(config.type).sendDate || '-' }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex flex-wrap gap-3">
                                    <Button
                                        :label="getTypeState(config.type).hasTemplate ? $t('Edit Configuration') : $t('Configure')"
                                        :icon="getTypeState(config.type).hasTemplate ? 'pi pi-pencil' : 'pi pi-cog'"
                                        :severity="getTypeState(config.type).hasTemplate ? 'secondary' : 'success'"
                                        @click="openEditModal(config.type)"
                                    />
                                    <Button
                                        :label="$t('Preview')"
                                        icon="pi pi-eye"
                                        severity="info"
                                        outlined
                                        :disabled="!getTypeState(config.type).hasTemplate"
                                        @click="openPreviewModal(config.type)"
                                    />
                                    <Button
                                        :label="$t('Send Newsletter')"
                                        icon="pi pi-send"
                                        severity="primary"
                                        :disabled="!getTypeState(config.type).hasTemplate || !getTypeState(config.type).hasRecipients || isSending[config.key]"
                                        :loading="isSending[config.key]"
                                        @click="sendNewsletter(config.type)"
                                    />
                                </div>
                            </div>
                        </TabPanel>
                    </TabView>
                </template>
            </Card>
        </div>

        <!-- Edit Modal (Template + Recipients) -->
        <NewsletterEditModal
            v-model="showEditModal"
            :template-type="currentType"
            :newsletter-id="currentNewsletterId"
            :existing-data="currentNewsletterData"
            :clients="clients"
            @saved="handleNewsletterSaved"
        />

        <!-- Preview Modal -->
        <PreviewModal
            v-model="showPreviewModal"
            :template-type="currentType"
            :newsletter-id="currentNewsletterId"
            :language="currentPreviewLanguage"
            :available-languages="availableLanguages"
        />
    </div>
</template>

<style scoped>
:deep(.newsletter-tabs .p-tabview-panels) {
    padding: 0;
}

:deep(.newsletter-tabs .p-tabview-nav) {
    border-bottom: 2px solid var(--surface-200);
}

:deep(.newsletter-tabs .p-tabview-nav-link) {
    padding: 1rem 1.5rem;
}
</style>
